/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */

/**
 * Provides a simple utility which allows for determining if an application is running
 * on a phone / mobile device.
 *
 * ```typescript
 *
 *    import {isPhone} from '@ion/core/src/capabilities';
 *
 *    isPhone(); // true if (globalThis / window).innerWidth is 414 or less
 *    isPhone(500); // true if (globalThis / window).innerWidth is 500 or less
 *
 * ```
 *
 * @param   {number} breakpoint An optional mobile breakpoint against which to test.
 *          Defaults to 414 standard.
 * @returns {boolean} True if the device is of a mobile form factor, otherwise, false.
 */
export const isPhone = (breakpoint = 414): boolean => {
  return __context__()?.width <= breakpoint;
};

/**
 * Provides a simple utility which allows for determining if an application is running
 * on a desktop device.
 *
 * ```typescript
 *
 *    import {isPhone} from '@ion/core/src/capabilities';
 *
 *    isDesktop(); // true if (globalThis / window).innerWidth is 1440 or less
 *    isDesktop(1500); // true if (globalThis / window).innerWidth is 1500 or less
 *
 * ```
 *
 * @param   {number} breakpoint An optional desktop breakpoint against which to test.
 *          Defaults to 1440 standard.
 * @returns {boolean} True if the device is of a desktop factor, otherwise, false.
 */
export const isDesktop = (breakpoint = 1440): boolean => {
  return __context__()?.width >= breakpoint;
};

/**
 * Provides a simple utility to get the screen Height and Width.
 *
 * ```typescript
 *
 *    import {getDimensions} from '@ion/core/src/capabilities';
 *
 *    const {width, height} = getDimensions();
 *    console.log(width, height); // 360 640
 *
 * ```
 *
 */
export const getDimensions = (): {width: number, height: number} => {
  return {
    width  : __context__()?.width,
    height : __context__()?.height
  };
};

/**
 * @private
 */
let _screen: Screen;

/**
 * @private
 */
export const __context__  = (screen?: Screen): Screen => {
  if (screen) {
    _screen = screen;
  }
  return _screen || globalThis.screen;
};
